import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataTable from 'react-data-table-component'
import { Spinner } from 'react-bootstrap'
import { FaUserGraduate as CarometroIcon } from 'react-icons/fa'
import { useSearchParams } from 'react-router-dom'

import { AdministracaoService } from '@core/services'
import { CarometroAluno, CarometroAlunosPagination } from '@core/types/services'
import { Message } from '@presentation/components/molecules'
import { useModal } from '@presentation/hooks'
import { Page } from '@presentation/components/templates'
import { Filter } from '@presentation/components/organisms'
import { PageProps } from '@presentation/types/components/templates'
import { FilterProps } from '@presentation/types/components/organisms'
import { FetchProps } from '@presentation/types/hooks'
import { parseToRequest, toTitleCase } from '@presentation/utils'

import { AlunoCard, AlunoDetailsModal } from './components'
import * as S from './styles'
import { useCarometroPhotoCache } from './use-carometro-photo-cache'

const RELEVANT_CAROMETRO_SITUACOES = [
  'Matriculado',
  'Concludente',
  'Trancado',
  'Afastado',
  'Reingresso',
  'Abandono',
  'Cancelamento Voluntário',
  'Cancelado Compulsoriamente',
  'Aguardando Colação de Grau',
  'Concluído',
  'Formado'
]

const normalizeCarometroAluno = (item: CarometroAluno): CarometroAluno => ({
  ...item,
  id:
    item.id ??
    item.pessoaIdentificacaoId ??
    item.pessoa_identificacao_id ??
    item.pessoaId ??
    item.pessoa_id,
  pessoaId: item.pessoaId ?? item.pessoa_id,
  pessoaIdentificacaoId:
    item.pessoaIdentificacaoId ?? item.pessoa_identificacao_id,
  foto: item.foto ?? item.fotoUrl ?? item.foto_url ?? null,
  nome: item.nome ?? item.nomeCompleto ?? item.nome_completo ?? '',
  nomeResumido:
    item.nomeResumido ??
    item.nome_resumido ??
    item.nome ??
    item.nomeCompleto ??
    item.nome_completo ??
    '',
  telefone: item.telefone ?? '',
  email: item.email ?? '',
  matricula: item.matricula ?? '',
  curso: item.curso ?? '',
  situacao: item.situacao ?? '',
  pessoaTipo: item.pessoaTipo ?? item.pessoa_tipo ?? ''
})

const AUTO_PER_PAGE_DEBOUNCE_MS = 160

const getResponseItems = (response: any): CarometroAluno[] => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data
  const items = nestedData?.items ?? data?.items ?? []

  if (!Array.isArray(items)) return []

  return items.map((item: CarometroAluno) => normalizeCarometroAluno(item))
}

const getResponseTotal = (response: any, items: CarometroAluno[]) => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data
  const pagination = nestedData?.pagination ?? data?.pagination ?? {}

  return pagination?.total ?? nestedData?.total ?? data?.total ?? items.length
}

const getResponsePagination = (
  response: any,
  totalItems: number
): CarometroAlunosPagination => {
  const data = response?.data ?? response
  const nestedData = data?.data ?? data
  const pagination = nestedData?.pagination ?? data?.pagination ?? {}

  const currentPage = Number(
    pagination?.currentPage ?? pagination?.current_page ?? pagination?.page ?? 1
  )
  const perPage = Number(
    pagination?.perPage ?? pagination?.per_page ?? pagination?.limit ?? 20
  )
  const total = Number(
    pagination?.total ?? nestedData?.total ?? data?.total ?? totalItems
  )
  const totalInPage = Number(
    pagination?.totalInPage ??
      pagination?.total_in_page ??
      nestedData?.items?.length ??
      0
  )
  const lastPage = Number(
    pagination?.pageCount ??
      pagination?.page_count ??
      pagination?.lastPage ??
      pagination?.last_page ??
      (perPage > 0 ? Math.ceil(total / perPage) : 1)
  )
  const from = Number(
    pagination?.from ?? (total === 0 ? 0 : (currentPage - 1) * perPage + 1)
  )
  const to = Number(
    pagination?.to ??
      (totalInPage > 0
        ? from + totalInPage - 1
        : Math.min(currentPage * perPage, total))
  )

  return {
    page: currentPage,
    perPage,
    total,
    totalInPage,
    lastPage,
    from,
    to,
    hasMorePages:
      Boolean(pagination?.hasMorePages ?? pagination?.has_more_pages) ||
      currentPage < lastPage
  }
}

const mapParamsToRequest = (params: any) => {
  const { sortBy, sortDir, perPage, ...rest } = params

  const optionalParams = Object.fromEntries(
    Object.entries(rest).filter(
      ([, value]) => value !== undefined && value !== ''
    )
  )

  const mergedParams = {
    per_page: perPage ?? 20,
    sort_by: sortBy,
    sort_dir: sortDir,
    ...(params.page !== undefined ? { page: params.page } : {}),
    ...optionalParams
  }

  return parseToRequest(mergedParams)
}

const getRenderedColumnsCount = (gridElement: HTMLDivElement) => {
  const templateColumns = getComputedStyle(gridElement).gridTemplateColumns

  if (!templateColumns) return 0

  return templateColumns
    .split(' ')
    .map(column => column.trim())
    .filter(Boolean).length
}

const getAutoPerPage = (columnsCount: number) => {
  if (columnsCount <= 2) return 8
  if (columnsCount <= 4) return columnsCount * 3

  return columnsCount * 2
}

const CarometroAlunosPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { isOpen, openModal, closeModal } = useModal()
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)
  const resizeDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const lastAutoPerPageRef = useRef<number | null>(null)
  const currentPerPageRef = useRef<number>(12)
  const [data, setData] = useState<CarometroAluno[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [selectedAluno, setSelectedAluno] = useState<CarometroAluno | null>(
    null
  )
  const [pagination, setPagination] = useState<CarometroAlunosPagination>({
    page: 1,
    perPage: 12,
    total: 0,
    totalInPage: 0,
    lastPage: 1,
    from: 0,
    to: 0,
    hasMorePages: false
  })
  const { getPhotoSrc } = useCarometroPhotoCache(data, selectedAluno)

  useEffect(() => {
    if (pagination.perPage > 0) {
      currentPerPageRef.current = pagination.perPage
    }
  }, [pagination.perPage])

  const fetchData = useCallback(async (input?: FetchProps) => {
    setIsFetching(true)
    setErrorMessage(undefined)

    const params = Object.assign(
      {},
      {
        page: 1,
        perPage: currentPerPageRef.current,
        sortBy: 'nome'
      },
      input?.options,
      input?.queries
    )

    try {
      const response = await AdministracaoService.obterCarometroAlunos({
        params: mapParamsToRequest(params)
      })
      const items = getResponseItems(response)
      const total = getResponseTotal(response, items)

      setData(items)
      setPagination(getResponsePagination(response, total))
    } catch (error) {
      setData([])
      setPagination({
        page: 1,
        perPage: currentPerPageRef.current,
        total: 0,
        totalInPage: 0,
        lastPage: 1,
        from: 0,
        to: 0,
        hasMorePages: false
      })
      setErrorMessage(
        'Não foi possível carregar os alunos agora. Recarregue a página e tente novamente.'
      )
      console.error('Erro ao carregar carômetro de alunos.', error)
    } finally {
      setIsFetching(false)
      setHasLoadedOnce(true)
    }
  }, [])

  const perPageOptions = useMemo(() => {
    return Array.from(new Set([8, 12, 16, 24, pagination.perPage])).sort(
      (left, right) => left - right
    )
  }, [pagination.perPage])

  const selectedAlunoPhoto = useMemo(
    () => getPhotoSrc(selectedAluno),
    [getPhotoSrc, selectedAluno]
  )

  const openAlunoDetails = (aluno: CarometroAluno) => {
    setSelectedAluno(aluno)
    openModal()
  }

  const handlePageChange = async (page: number) => {
    const currentQueries = Object.fromEntries(searchParams)

    await fetchData({
      options: {
        page,
        perPage: pagination.perPage
      },
      queries: currentQueries
    })
  }

  const handlePerPageChange = async (nextPerPage: number) => {
    const currentQueries = Object.fromEntries(searchParams)

    await fetchData({
      options: {
        page: 1,
        perPage: nextPerPage
      },
      queries: currentQueries
    })
  }

  useEffect(() => {
    if (!gridElement || typeof ResizeObserver === 'undefined') return

    const applyAutoPerPage = () => {
      const columnsCount = getRenderedColumnsCount(gridElement)

      if (columnsCount <= 0) return

      const nextPerPage = getAutoPerPage(columnsCount)

      if (
        nextPerPage === pagination.perPage ||
        nextPerPage === lastAutoPerPageRef.current
      ) {
        return
      }

      lastAutoPerPageRef.current = nextPerPage
      currentPerPageRef.current = nextPerPage

      fetchData({
        options: {
          page: 1,
          perPage: nextPerPage
        },
        queries: Object.fromEntries(searchParams)
      }).catch(() => undefined)
    }

    const observer = new ResizeObserver(() => {
      if (resizeDebounceTimeoutRef.current) {
        clearTimeout(resizeDebounceTimeoutRef.current)
      }

      resizeDebounceTimeoutRef.current = setTimeout(() => {
        applyAutoPerPage()
      }, AUTO_PER_PAGE_DEBOUNCE_MS)
    })

    observer.observe(gridElement)
    applyAutoPerPage()

    return () => {
      observer.disconnect()

      if (resizeDebounceTimeoutRef.current) {
        clearTimeout(resizeDebounceTimeoutRef.current)
      }
    }
  }, [fetchData, gridElement, pagination.perPage, searchParams])

  const pageConfigs: PageProps = {
    title: 'Carômetro de Alunos',
    description:
      'Página para localização de alunos por foto, nome, telefone e informações acadêmicas.',
    icon: <CarometroIcon />
  }

  const filterConfigs: FilterProps = {
    filters: [
      {
        type: {
          value: 'nome',
          label: 'Nome'
        },
        description: {
          type: 'input',
          configs: {
            type: 'text'
          }
        }
      },
      {
        type: {
          value: 'curso',
          label: 'Curso'
        },
        description: {
          type: 'select-api',
          configs: {
            path: '/cursos?curso_tipo_slug=integrado&sort_by=nome',
            isPaginate: true,
            getOptionLabel: option => option.nome,
            getOptionValue: option => option.id,
            searchKey: 'nome',
            serializeKey: 'nome'
          }
        }
      },
      {
        type: {
          value: 'matricula',
          label: 'Matrícula'
        },
        description: {
          type: 'input',
          configs: {
            type: 'text'
          }
        }
      },
      {
        type: {
          value: 'situacao',
          label: 'Situação'
        },
        description: {
          type: 'select-api',
          configs: {
            path: '/situacoes?attr=1&sort_by=descricao',
            isPaginate: false,
            getOptionLabel: option => option.descricao,
            getOptionValue: option => option.id,
            searchKey: 'descricao',
            serializeKey: 'descricao',
            filterOptions: options =>
              options.filter(option =>
                RELEVANT_CAROMETRO_SITUACOES.includes(option.descricao)
              )
          }
        }
      }
    ],
    fetchData,
    defaultQueries: {
      page: 1,
      perPage: currentPerPageRef.current,
      sortBy: 'nome'
    },
    hasStatus: false
  }

  return (
    <Page {...pageConfigs}>
      <Filter {...filterConfigs} />

      {errorMessage && (
        <S.StateWrapper>
          <Message variant="danger">{errorMessage}</Message>
        </S.StateWrapper>
      )}

      {!errorMessage && (
        <>
          {isFetching && data.length > 0 && (
            <S.ResultsHeader>
              <S.InlineLoading>
                <Spinner animation="border" size="sm" />
                Atualizando resultados...
              </S.InlineLoading>
            </S.ResultsHeader>
          )}

          {!hasLoadedOnce || (isFetching && data.length === 0) ? (
            <S.LoadingState>
              <Spinner
                animation="border"
                style={{ width: '3rem', height: '3rem' }}
              />
              <span>Carregando alunos...</span>
            </S.LoadingState>
          ) : data.length === 0 ? (
            <S.EmptyState>
              Nenhum aluno foi encontrado para os filtros aplicados.
            </S.EmptyState>
          ) : (
            <>
              <S.Grid ref={setGridElement}>
                {data.map((aluno, index) => (
                  <AlunoCard
                    key={
                      aluno.pessoaIdentificacaoId ??
                      aluno.id ??
                      `${aluno.matricula}-${index}`
                    }
                    aluno={aluno}
                    photoSrc={getPhotoSrc(aluno)}
                    displayName={toTitleCase(
                      aluno.nomeResumido ?? aluno.nome ?? 'Não informado'
                    )}
                    onClick={() => openAlunoDetails(aluno)}
                  />
                ))}
              </S.Grid>

              <S.PaginationDataTableWrapper>
                <DataTable
                  key={`${pagination.page}-${pagination.perPage}-${pagination.total}`}
                  columns={[]}
                  data={data}
                  noHeader
                  noTableHead
                  pagination
                  paginationServer
                  paginationTotalRows={pagination.total}
                  paginationDefaultPage={pagination.page}
                  paginationPerPage={pagination.perPage}
                  paginationRowsPerPageOptions={perPageOptions}
                  paginationComponentOptions={{
                    rowsPerPageText: 'Resultados por página',
                    rangeSeparatorText: 'de'
                  }}
                  onChangePage={page => {
                    handlePageChange(page).catch(() => undefined)
                  }}
                  onChangeRowsPerPage={rowsPerPage => {
                    handlePerPageChange(rowsPerPage).catch(() => undefined)
                  }}
                />
              </S.PaginationDataTableWrapper>
            </>
          )}
        </>
      )}

      <AlunoDetailsModal
        aluno={selectedAluno}
        isOpen={isOpen}
        onClose={closeModal}
        photoSrc={selectedAlunoPhoto}
      />
    </Page>
  )
}

export default CarometroAlunosPage
